import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- sessionStorage persistence ---

  const getNavigationHistory = useCallback(() => {
    try {
      const stored = sessionStorage.getItem('navigationHistory');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const saveNavigationHistory = useCallback((history) => {
    try {
      sessionStorage.setItem('navigationHistory', JSON.stringify(history));
    } catch { /* noop */ }
  }, []);

  const [navigationHistory, setNavigationHistory] = useState(() => getNavigationHistory());

  const updateHistory = useCallback((newHistory) => {
    setNavigationHistory(newHistory);
    saveNavigationHistory(newHistory);
  }, [saveNavigationHistory]);

  // --- Current page helpers ---

  const getCurrentPageType = useCallback(() => {
    const pathname = location.pathname;
    if (pathname.startsWith('/article/')) return 'article';
    if (pathname.startsWith('/topics/')) return 'topic';
    if (pathname.startsWith('/search')) return 'search';
    if (pathname === '/daily') return 'daily';
    if (pathname === '/briefing') return 'briefing';
    if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
    return 'other';
  }, [location.pathname]);

  const getCurrentPageId = useCallback(() => {
    const match = location.pathname.match(/\/(article|topics)\/([^/]+)/);
    return match ? match[2] : null;
  }, [location.pathname]);

  const getCurrentContext = useCallback(() => {
    return location.state?.context || {};
  }, [location.state]);

  const getCurrentPageState = useCallback(() => {
    return location.state?.pageState || {};
  }, [location.state]);

  // Snapshot the current page as a history entry using the actual URL
  // (no type→path reconstruction — every route round-trips correctly)
  const createCurrentPageEntry = useCallback(() => {
    return {
      type: getCurrentPageType(),
      id: getCurrentPageId()?.toString(),
      path: location.pathname + location.search,
      context: { ...getCurrentContext() },
      pageState: { ...getCurrentPageState() },
      timestamp: Date.now()
    };
  }, [location.pathname, location.search, getCurrentPageType, getCurrentPageId, getCurrentContext, getCurrentPageState]);

  // --- History stack ---

  const addToHistory = useCallback((entry) => {
    const newHistory = [...navigationHistory];

    const existingIndex = newHistory.findIndex(item => item.path === entry.path);
    if (existingIndex !== -1) {
      newHistory.splice(existingIndex, 1);
    }

    newHistory.push(entry);

    if (newHistory.length > 20) {
      newHistory.shift();
    }

    updateHistory(newHistory);
  }, [navigationHistory, updateHistory]);

  // --- Navigation actions ---

  const navigateToArticle = useCallback((articleId, options = {}) => {
    const {
      context = {},
      pageState = {},
      preserveHistory = true,
      replace = false
    } = options;

    if (preserveHistory && !replace) {
      addToHistory(createCurrentPageEntry());
    }

    navigate(`/article/${articleId}`, {
      state: { navigationId: Date.now(), context, pageState },
      replace
    });
  }, [navigate, createCurrentPageEntry, addToHistory]);

  const navigateToTopic = useCallback((topicId, options = {}) => {
    const {
      context = {},
      pageState = {},
      preserveHistory = true,
      replace = false
    } = options;

    if (preserveHistory && !replace) {
      addToHistory(createCurrentPageEntry());
    }

    navigate(`/topics/${topicId}`, {
      state: { navigationId: Date.now(), context, pageState },
      replace
    });
  }, [navigate, createCurrentPageEntry, addToHistory]);

  const goBack = useCallback(() => {
    const history = getNavigationHistory();

    if (history.length > 0) {
      const previousEntry = history[history.length - 1];
      updateHistory(history.slice(0, -1));

      navigate(previousEntry.path, {
        state: {
          navigationId: Date.now(),
          context: previousEntry.context,
          pageState: previousEntry.pageState,
          restoredFromHistory: true
        },
        replace: true
      });
    } else {
      navigate('/dashboard');
    }
  }, [navigate, getNavigationHistory, updateHistory]);

  const canGoBack = useCallback(() => {
    return getNavigationHistory().length > 0;
  }, [getNavigationHistory]);

  const getNavigationContext = useCallback(() => {
    return {
      context: getCurrentContext(),
      pageState: getCurrentPageState(),
      canGoBack: canGoBack(),
      historyLength: getNavigationHistory().length,
      currentPageType: getCurrentPageType(),
      currentPageId: getCurrentPageId()
    };
  }, [getCurrentContext, getCurrentPageState, canGoBack, getNavigationHistory, getCurrentPageType, getCurrentPageId]);

  const clearHistory = useCallback(() => {
    updateHistory([]);
  }, [updateHistory]);

  return {
    navigateToArticle,
    navigateToTopic,
    goBack,
    getNavigationContext,
    canGoBack,
    clearHistory,
    navigationHistory: getNavigationHistory()
  };
};

export default useNavigation;
